// src/store/slices/growth/visitorSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

// =============================================
// ASYNC THUNKS
// =============================================

export const getVisitors = createAsyncThunk(
    'visitors/getVisitors',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.VISITORS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitors retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch visitors',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get visitors error:', error);
            let errorMessage = 'Failed to fetch visitors';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch visitors';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getVisitor = createAsyncThunk(
    'visitors/getVisitor',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.VISITOR(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor retrieved successfully',
                    visitor: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch visitor',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get visitor error:', error);
            let errorMessage = 'Failed to fetch visitor';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch visitor';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createVisitor = createAsyncThunk(
    'visitors/createVisitor',
    async (visitorData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Creating visitor with data:', visitorData);

            const response = await apiClient.post(API_ENDPOINTS.VISITOR_CREATE, visitorData);
            console.log('✅ Create visitor response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor created successfully',
                    visitor: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create visitor',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Create visitor error:', error);
            let errorMessage = 'Failed to create visitor';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to create visitor';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateVisitor = createAsyncThunk(
    'visitors/updateVisitor',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Updating visitor:', id, data);

            const response = await apiClient.put(API_ENDPOINTS.VISITOR_UPDATE(id), data);
            console.log('✅ Update visitor response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor updated successfully',
                    visitor: responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update visitor',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Update visitor error:', error);
            let errorMessage = 'Failed to update visitor';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to update visitor';
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteVisitor = createAsyncThunk(
    'visitors/deleteVisitor',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Deleting visitor:', id);

            const response = await apiClient.delete(API_ENDPOINTS.VISITOR_DELETE(id));
            console.log('✅ Delete visitor response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete visitor',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Delete visitor error:', error);
            let errorMessage = 'Failed to delete visitor';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to delete visitor';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendVisitorMessage = createAsyncThunk(
    'visitors/sendMessage',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Sending visitor message:', id);

            const response = await apiClient.post(API_ENDPOINTS.VISITOR_SEND_MESSAGE(id));
            console.log('✅ Send message response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message sent successfully',
                    messageData: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Send message error:', error);
            let errorMessage = 'Failed to send message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to send message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const convertVisitor = createAsyncThunk(
    'visitors/convert',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Converting visitor:', id);

            const response = await apiClient.post(API_ENDPOINTS.VISITOR_CONVERT(id));
            console.log('✅ Convert visitor response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor converted successfully',
                    visitor: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to convert visitor',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Convert visitor error:', error);
            let errorMessage = 'Failed to convert visitor';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to convert visitor';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const resetVisitorJourney = createAsyncThunk(
    'visitors/resetJourney',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Resetting visitor journey:', id);

            const response = await apiClient.post(API_ENDPOINTS.VISITOR_RESET(id));
            console.log('✅ Reset journey response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Journey reset successfully',
                    visitor: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to reset journey',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Reset journey error:', error);
            let errorMessage = 'Failed to reset journey';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to reset journey';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getVisitorStats = createAsyncThunk(
    'visitors/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Fetching visitor stats:', params);

            const response = await apiClient.get(API_ENDPOINTS.VISITOR_STATS, { params });
            console.log('✅ Visitor stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor statistics retrieved',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch visitor statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Get visitor stats error:', error);
            let errorMessage = 'Failed to fetch visitor statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch visitor statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getVisitorSources = createAsyncThunk(
    'visitors/getSources',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Fetching visitor sources:', params);

            const response = await apiClient.get(API_ENDPOINTS.VISITOR_SOURCES, { params });
            console.log('✅ Visitor sources response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Sources retrieved successfully',
                    sources: data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch sources',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Get visitor sources error:', error);
            let errorMessage = 'Failed to fetch sources';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch sources';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendBulkSms = createAsyncThunk(
    'visitors/sendBulkSms',
    async ({ visitor_ids, message, sender_id = null }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Sending bulk SMS:', { visitor_ids, message });

            const response = await apiClient.post(API_ENDPOINTS.VISITOR_BULK_SMS, {
                visitor_ids,
                message,
                sender_id
            });
            console.log('✅ Bulk SMS response:', response.data);

            const { status, message: responseMessage, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: responseMessage || 'Bulk SMS sent successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: responseMessage || 'Failed to send bulk SMS',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Send bulk SMS error:', error);
            let errorMessage = 'Failed to send bulk SMS';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to send bulk SMS';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportVisitor = createAsyncThunk(
    'visitors/export',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Exporting visitor:', id);

            const response = await apiClient.get(API_ENDPOINTS.VISITOR_EXPORT(id));
            console.log('✅ Export visitor response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Visitor exported successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export visitor',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Export visitor error:', error);
            let errorMessage = 'Failed to export visitor';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to export visitor';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ===== SEQUENCE THUNKS =====

export const getVisitorSequences = createAsyncThunk(
    'visitors/getSequences',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Fetching visitor sequences:', params);

            const response = await apiClient.get(API_ENDPOINTS.VISITOR_SEQUENCES, { params });
            console.log('✅ Visitor sequences response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Sequences retrieved successfully',
                    sequences: data || []
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch sequences',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Get visitor sequences error:', error);
            let errorMessage = 'Failed to fetch sequences';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to fetch sequences';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const saveVisitorSequences = createAsyncThunk(
    'visitors/saveSequences',
    async (sequences, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('🔄 Saving visitor sequences:', sequences);

            const response = await apiClient.post(API_ENDPOINTS.VISITOR_SEQUENCE, { sequences });
            console.log('✅ Save sequences response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Sequences saved successfully',
                    sequences: sequences
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to save sequences',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('❌ Save visitor sequences error:', error);
            let errorMessage = 'Failed to save sequences';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                errorMessage = error.response.data?.message || 'Failed to save sequences';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    visitors: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    currentVisitor: null,
    stats: null,
    sources: [],
    sequences: [],
    isLoading: false,
    isSubmitting: false,
    isSequencesLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const visitorSlice = createSlice({
    name: 'visitors',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearVisitorState: (state) => {
            state.visitors = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.currentVisitor = null;
            state.stats = null;
            state.sources = [];
            state.sequences = [];
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.isSubmitting = false;
            state.isSequencesLoading = false;
        },
        clearCurrentVisitor: (state) => {
            state.currentVisitor = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET VISITORS =====
            .addCase(getVisitors.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getVisitors.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};

                state.visitors = responseData.data || [];
                state.pagination = responseData.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = payload.message || 'Visitors retrieved successfully';
                state.apiMessage = payload.message || 'Visitors retrieved successfully';
                state.error = null;
            })
            .addCase(getVisitors.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch visitors';
                state.apiMessage = action.payload?.message || 'Failed to fetch visitors';
                state.successMessage = null;
                state.visitors = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET VISITOR =====
            .addCase(getVisitor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getVisitor.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentVisitor = payload.visitor || null;
                state.successMessage = payload.message || 'Visitor retrieved successfully';
                state.apiMessage = payload.message || 'Visitor retrieved successfully';
                state.error = null;
            })
            .addCase(getVisitor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch visitor';
                state.apiMessage = action.payload?.message || 'Failed to fetch visitor';
                state.successMessage = null;
                state.currentVisitor = null;
            })

            // ===== CREATE VISITOR =====
            .addCase(createVisitor.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createVisitor.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Visitor created successfully';
                state.apiMessage = payload.message || 'Visitor created successfully';
                state.error = null;

                if (payload.visitor) {
                    state.visitors = [payload.visitor, ...state.visitors];
                }
            })
            .addCase(createVisitor.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create visitor';
                state.apiMessage = action.payload?.message || 'Failed to create visitor';
                state.successMessage = null;
            })

            // ===== UPDATE VISITOR =====
            .addCase(updateVisitor.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateVisitor.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Visitor updated successfully';
                state.apiMessage = payload.message || 'Visitor updated successfully';
                state.error = null;

                if (payload.visitor) {
                    state.visitors = state.visitors.map(v =>
                        v.id === payload.visitor.id ? payload.visitor : v
                    );
                    if (state.currentVisitor?.id === payload.visitor.id) {
                        state.currentVisitor = payload.visitor;
                    }
                }
            })
            .addCase(updateVisitor.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update visitor';
                state.apiMessage = action.payload?.message || 'Failed to update visitor';
                state.successMessage = null;
            })

            // ===== DELETE VISITOR =====
            .addCase(deleteVisitor.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteVisitor.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Visitor deleted successfully';
                state.apiMessage = payload.message || 'Visitor deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.visitors = state.visitors.filter(v => v.id !== payload.id);
                    if (state.currentVisitor?.id === payload.id) {
                        state.currentVisitor = null;
                    }
                }
            })
            .addCase(deleteVisitor.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete visitor';
                state.apiMessage = action.payload?.message || 'Failed to delete visitor';
                state.successMessage = null;
            })

            // ===== SEND MESSAGE =====
            .addCase(sendVisitorMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendVisitorMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message sent successfully';
                state.apiMessage = payload.message || 'Message sent successfully';
                state.error = null;
            })
            .addCase(sendVisitorMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send message';
                state.apiMessage = action.payload?.message || 'Failed to send message';
                state.successMessage = null;
            })

            // ===== CONVERT VISITOR =====
            .addCase(convertVisitor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(convertVisitor.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Visitor converted successfully';
                state.apiMessage = payload.message || 'Visitor converted successfully';
                state.error = null;

                if (payload.visitor) {
                    state.visitors = state.visitors.map(v =>
                        v.id === payload.visitor.id ? payload.visitor : v
                    );
                    if (state.currentVisitor?.id === payload.visitor.id) {
                        state.currentVisitor = payload.visitor;
                    }
                }
            })
            .addCase(convertVisitor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to convert visitor';
                state.apiMessage = action.payload?.message || 'Failed to convert visitor';
                state.successMessage = null;
            })

            // ===== RESET JOURNEY =====
            .addCase(resetVisitorJourney.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(resetVisitorJourney.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Journey reset successfully';
                state.apiMessage = payload.message || 'Journey reset successfully';
                state.error = null;

                if (payload.visitor) {
                    state.visitors = state.visitors.map(v =>
                        v.id === payload.visitor.id ? payload.visitor : v
                    );
                    if (state.currentVisitor?.id === payload.visitor.id) {
                        state.currentVisitor = payload.visitor;
                    }
                }
            })
            .addCase(resetVisitorJourney.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to reset journey';
                state.apiMessage = action.payload?.message || 'Failed to reset journey';
                state.successMessage = null;
            })

            // ===== GET STATS =====
            .addCase(getVisitorStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getVisitorStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = payload.stats || null;
                state.successMessage = payload.message || 'Visitor statistics retrieved';
                state.apiMessage = payload.message || 'Visitor statistics retrieved';
                state.error = null;
            })
            .addCase(getVisitorStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch visitor statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch visitor statistics';
                state.successMessage = null;
                state.stats = null;
            })

            // ===== GET SOURCES =====
            .addCase(getVisitorSources.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getVisitorSources.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.sources = payload.sources || [];
                state.successMessage = payload.message || 'Sources retrieved successfully';
                state.apiMessage = payload.message || 'Sources retrieved successfully';
                state.error = null;
            })
            .addCase(getVisitorSources.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch sources';
                state.apiMessage = action.payload?.message || 'Failed to fetch sources';
                state.successMessage = null;
                state.sources = [];
            })

            // ===== SEND BULK SMS =====
            .addCase(sendBulkSms.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendBulkSms.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Bulk SMS sent successfully';
                state.apiMessage = payload.message || 'Bulk SMS sent successfully';
                state.error = null;
            })
            .addCase(sendBulkSms.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send bulk SMS';
                state.apiMessage = action.payload?.message || 'Failed to send bulk SMS';
                state.successMessage = null;
            })

            // ===== EXPORT VISITOR =====
            .addCase(exportVisitor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportVisitor.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Visitor exported successfully';
                state.apiMessage = payload.message || 'Visitor exported successfully';
                state.error = null;
            })
            .addCase(exportVisitor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export visitor';
                state.apiMessage = action.payload?.message || 'Failed to export visitor';
                state.successMessage = null;
            })

            // ===== GET SEQUENCES =====
            .addCase(getVisitorSequences.pending, (state) => {
                state.isSequencesLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getVisitorSequences.fulfilled, (state, action) => {
                state.isSequencesLoading = false;
                const payload = action.payload || {};
                state.sequences = payload.sequences || [];
                state.successMessage = payload.message || 'Sequences retrieved successfully';
                state.apiMessage = payload.message || 'Sequences retrieved successfully';
                state.error = null;
            })
            .addCase(getVisitorSequences.rejected, (state, action) => {
                state.isSequencesLoading = false;
                state.error = action.payload?.message || 'Failed to fetch sequences';
                state.apiMessage = action.payload?.message || 'Failed to fetch sequences';
                state.successMessage = null;
                state.sequences = [];
            })

            // ===== SAVE SEQUENCES =====
            .addCase(saveVisitorSequences.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(saveVisitorSequences.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const payload = action.payload || {};
                state.sequences = payload.sequences || [];
                state.successMessage = payload.message || 'Sequences saved successfully';
                state.apiMessage = payload.message || 'Sequences saved successfully';
                state.error = null;
            })
            .addCase(saveVisitorSequences.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to save sequences';
                state.apiMessage = action.payload?.message || 'Failed to save sequences';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearVisitorState,
    clearCurrentVisitor,
} = visitorSlice.actions;

// =============================================
// SELECTORS
// =============================================

export const selectVisitors = (state) => state.visitors?.visitors || [];
export const selectVisitorPagination = (state) => state.visitors?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectCurrentVisitor = (state) => state.visitors?.currentVisitor || null;
export const selectVisitorStats = (state) => state.visitors?.stats || null;
export const selectVisitorSources = (state) => state.visitors?.sources || [];
export const selectVisitorSequences = (state) => state.visitors?.sequences || [];
export const selectVisitorsLoading = (state) => state.visitors?.isLoading || false;
export const selectVisitorsSubmitting = (state) => state.visitors?.isSubmitting || false;
export const selectVisitorsError = (state) => state.visitors?.error || null;
export const selectVisitorsApiMessage = (state) => state.visitors?.apiMessage || null;
export const selectVisitorSequencesLoading = (state) => state.visitors?.isSequencesLoading || false;

export default visitorSlice.reducer;